import { useCallback, useState } from 'react';
import {
  readAutoHideDiscussionPanel,
  readFileReviewPanelPlacement,
  writeAutoHideDiscussionPanel,
  writeFileReviewPanelPlacement,
  type FileReviewPanelPlacement
} from '@jarvis/platform-file-review';
import { PrefSegmentedControl, PrefSwitch } from '../components/PrefControls';
import '../theme/themePreferences.css';

const PLACEMENT_ITEMS: { text: string; value: FileReviewPanelPlacement }[] = [
  { text: 'Left side', value: 'left' },
  { text: 'Right side', value: 'right' },
  { text: 'Off', value: 'off' }
];

export function FileReviewPreferencesPanel() {
  const [placement, setPlacement] = useState<FileReviewPanelPlacement>(() => readFileReviewPanelPlacement());
  const [autoHide, setAutoHide] = useState(() => readAutoHideDiscussionPanel());

  const onPlacementChanged = useCallback((value: FileReviewPanelPlacement) => {
    setPlacement(value);
    writeFileReviewPanelPlacement(value);
  }, []);

  const onAutoHideChanged = useCallback((value: boolean) => {
    setAutoHide(value);
    writeAutoHideDiscussionPanel(value);
  }, []);

  return (
    <section className="theme-preferences" aria-labelledby="document-discussions-preferences-heading">
      <h3 id="document-discussions-preferences-heading" className="theme-preferences__title">
        Document discussions
      </h3>
      <p className="theme-preferences__hint">
        Choose whether and where the discussion panel appears in document viewers (PDF, spreadsheet, and Word).
      </p>
      <div className="theme-preferences__section">
        <span className="theme-preferences__label">Panel position</span>
        <PrefSegmentedControl
          className="theme-preferences__family-group"
          items={PLACEMENT_ITEMS}
          value={placement}
          aria-label="Panel position"
          onChange={onPlacementChanged}
        />
        <p className="theme-preferences__hint theme-preferences__hint--tight">
          When set to Off, discussions stay hidden until you turn them on from the viewer. Use the file-type icon
          next to the filename or the viewer toolbar.
        </p>
      </div>
      <div className="theme-preferences__section">
        <div className="theme-preferences__compact-row">
          <PrefSwitch
            checked={autoHide}
            disabled={placement === 'off'}
            onChange={onAutoHideChanged}
            aria-label="Auto-hide discussion panel"
          />
          <span className="theme-preferences__hint theme-preferences__hint--inline">
            Auto-hide discussion panel
          </span>
        </div>
        <p className="theme-preferences__hint theme-preferences__hint--tight">
          When enabled, the discussion panel stays collapsed until you move the pointer over the left or right
          edge (matching your panel position) or click the file-type icon in the title bar.
        </p>
      </div>
    </section>
  );
}
